import {
    EVENT_CONTRACT_WS_CHANNELS,
    MARGIN_WS_CHANNELS,
    type EventContractWsChannel,
    type MarginWsChannel,
    type WsChannel,
    type WsSubscribeParams,
    type WsSubscriberCallback,
    type WsUnsubscribeCallback,
} from 'models/ws';
import { arraysAreEqual } from 'utils/arrays';
import config from 'utils/config';

import WsService, { type WsClientLabel, type WsConnectionStateCallback } from './ws';
import type { WsMonitor } from './wsMonitor';

type QueuedEntry = {
    callback: WsConnectionStateCallback;
    realUnsub: (() => void) | null;
};

export interface WsDomainClient<TChannel extends WsChannel> {
    connect: () => void;
    /**
     * The underlying WsService preserves the same subscriber callback identity
     * when it automatically re-subscribes after a transport reconnect. Consumers
     * may treat the post-reconnect stream as a fresh subscription while still
     * receiving it through the original callback.
     */
    subscribe: (params: WsSubscribeParams<TChannel>, callback: WsSubscriberCallback) => WsUnsubscribeCallback;
    subscribeConnectionState: (callback: WsConnectionStateCallback) => () => void;
    findSubscriptionBySubscriber: WsService['findSubscriptionBySubscriber'];
    updateSubscription: WsService['updateSubscription'];
    isWsConnected: () => boolean;
    /**
     * Whether the socket is open AND this client has an attached, confirmed
     * subscription on exactly this channel. Scoped to the client's own
     * subscriptions so another domain client's socket doesn't make this client
     * report a channel it isn't receiving.
     */
    isConnected: (channel: TChannel) => boolean;
    /** Telemetry handle for this client's socket; null until it is lazily created. */
    getMonitor: () => WsMonitor | null;
    /**
     * Deactivates this client: detaches its subscriptions from the underlying
     * service (sending server-side unsubscribes when the socket is open) while
     * remembering them. The next connect() re-attaches everything with the
     * original callbacks. Used on logout so legacy channels go quiet.
     *
     * Auth-boundary clients also replace every socket that can carry
     * authenticated subscriptions, ensuring the next handshake reads the
     * current CSRF and frontend-session values.
     */
    disconnect: () => void;
}

/**
 * Lazily owns a WsService. Connection-state callbacks registered before the
 * service exists are queued and attached when the client first creates it.
 */
function createWsServiceOwner({
    label,
    getUrl,
}: {
    /**
     * WsMonitor label for this socket's owning domain.
     */
    label: WsClientLabel;
    getUrl: () => string;
}) {
    let ws: WsService | null = null;
    let queuedConnectionStateCallbacks: QueuedEntry[] = [];
    // Subscriptions currently attached to this service.
    let attachedCount = 0;

    function ensure(): WsService {
        if (!ws) {
            ws = new WsService(getUrl(), label);

            for (const entry of queuedConnectionStateCallbacks) {
                entry.realUnsub = ws.subscribeConnectionState(entry.callback);
            }
            queuedConnectionStateCallbacks = [];
        }
        return ws;
    }

    function connect(): void {
        const service = ensure();
        if (!service.isWsConnected()) {
            service.connect();
        }
    }

    function subscribeConnectionState(callback: WsConnectionStateCallback): () => void {
        if (ws) {
            return ws.subscribeConnectionState(callback);
        }

        const entry: QueuedEntry = { callback, realUnsub: null };
        queuedConnectionStateCallbacks.push(entry);

        return () => {
            if (entry.realUnsub) {
                entry.realUnsub();
            } else {
                queuedConnectionStateCallbacks = queuedConnectionStateCallbacks.filter((e) => e !== entry);
            }
        };
    }

    return {
        ensure,
        current: (): WsService | null => ws,
        connect,
        subscribeConnectionState,
        isWsConnected: (): boolean => ws?.isWsConnected() ?? false,
        retain: (): void => {
            attachedCount += 1;
        },
        release: (): void => {
            attachedCount = Math.max(0, attachedCount - 1);
        },
        closeIfUnused: (): void => {
            if (attachedCount === 0) {
                ws?.dispose();
            }
        },
        /**
         * Unconditionally closes the underlying socket, then immediately
         * reopens it if subscriptions remain attached. WsService.openConnection
         * reads the CSRF token and frontend-session id fresh from localStorage
         * on every connect, so reopening here gets a handshake scoped to the
         * current session instead of the one active when the socket was first
         * opened.
         */
        forceCloseForAuthBoundary: (): void => {
            const shouldReopen = attachedCount > 0;
            ws?.dispose();
            if (shouldReopen) {
                connect();
            }
        },
        resetForTest: (): void => {
            ws = null;
            queuedConnectionStateCallbacks = [];
            attachedCount = 0;
        },
    };
}

type WsServiceOwner = ReturnType<typeof createWsServiceOwner>;

type TrackedSubscription<TChannel extends WsChannel> = {
    params: WsSubscribeParams<TChannel>;
    callback: WsSubscriberCallback;
    /** null while detached (client inactive) — re-attached by connect(). */
    unsubscribe: WsUnsubscribeCallback | null;
};

function createWsClient<TChannel extends WsChannel>({
    clientName,
    service,
    validChannels,
    connectOnSubscribe = true,
    authBoundaryServices,
}: {
    clientName: string;
    service: WsServiceOwner;
    validChannels: readonly TChannel[];
    /**
     * When false, subscriptions registered through this client stay detached
     * from its service until an explicit connect() call activates the
     * client (login-gated usage). The server never sees this client's
     * subscribes before activation — even when another domain client is
     * already connected to the same endpoint.
     */
    connectOnSubscribe?: boolean;
    /**
     * Physical websocket services whose handshakes may carry authentication.
     * connect()/disconnect() rotate these services at login/logout boundaries,
     * even when a service belongs to another domain client.
     */
    authBoundaryServices?: readonly WsServiceOwner[];
}): { client: WsDomainClient<TChannel>; dispose: () => void; resetForTest: () => void } {
    const validChannelSet = new Set<string>(validChannels);
    const subscriptions = new Set<TrackedSubscription<TChannel>>();
    // Whether this client's subscriptions are attached to its service.
    // connect() activates, disconnect() deactivates; connect-on-subscribe
    // clients also activate on their first subscribe.
    let active = false;

    function assertValidSubscribeParams(params: WsSubscribeParams<TChannel>): void {
        const invalidChannel = params.channels.find((channel) => !validChannelSet.has(channel));
        if (invalidChannel) {
            throw new Error(`Invalid ${clientName} WS channel: ${invalidChannel}.`);
        }
    }

    function attach(entry: TrackedSubscription<TChannel>): void {
        if (entry.unsubscribe) {
            return;
        }
        entry.unsubscribe = service.ensure().subscribe(entry.params, entry.callback);
        service.retain();
    }

    function detach(entry: TrackedSubscription<TChannel>): void {
        if (!entry.unsubscribe) {
            return;
        }
        entry.unsubscribe();
        entry.unsubscribe = null;
        service.release();
    }

    function rotateAuthBoundaryServices(): void {
        for (const authService of authBoundaryServices ?? []) {
            authService.forceCloseForAuthBoundary();
        }
    }

    function connect(): void {
        if (!active) {
            // A previous authenticated connection may still be open or
            // mid-handshake. Replace every auth-capable socket before attaching
            // this client's subscriptions under the new session.
            rotateAuthBoundaryServices();
        }
        active = true;
        for (const entry of subscriptions) {
            attach(entry);
        }
        service.connect();
    }

    function subscribe(params: WsSubscribeParams<TChannel>, callback: WsSubscriberCallback): WsUnsubscribeCallback {
        assertValidSubscribeParams(params);
        if (connectOnSubscribe) {
            connect();
        }

        const entry: TrackedSubscription<TChannel> = { params, callback, unsubscribe: null };
        subscriptions.add(entry);
        if (active) {
            attach(entry);
        }

        return () => {
            const tracked = subscriptions.delete(entry);
            if (!tracked) {
                return false;
            }
            if (!entry.unsubscribe) {
                // Never attached (client inactive, e.g. logged out) — no
                // server-side subscription to tear down, but the
                // registration is gone, so this is a successful teardown
                // from the caller's perspective (e.g. OrderBook.dispose()
                // must still stop polling and reset).
                return true;
            }
            service.release();
            const unsubscribed = entry.unsubscribe();
            entry.unsubscribe = null;
            return unsubscribed;
        };
    }

    function subscribeConnectionState(callback: WsConnectionStateCallback): () => void {
        return service.subscribeConnectionState(callback);
    }

    // Neither of these throws when the service was never lazily initialized (no `connect()`/
    // `subscribe()` has happened yet on this client) — that's a normal "nothing subscribed yet"
    // state for a caller merely querying status, not a programming error, so it resolves the
    // same way a genuine "subscription not found" does: `null`/`false`.
    function findSubscriptionBySubscriber(
        subscriber: WsSubscriberCallback,
    ): ReturnType<WsService['findSubscriptionBySubscriber']> {
        return service.current()?.findSubscriptionBySubscriber(subscriber) ?? null;
    }

    function updateSubscription(
        ...args: Parameters<WsService['updateSubscription']>
    ): ReturnType<WsService['updateSubscription']> {
        return service.current()?.updateSubscription(...args) ?? false;
    }

    function isWsConnected(): boolean {
        return service.isWsConnected();
    }

    function isConnected(channel: TChannel): boolean {
        if (!service.current()?.isConnected(channel)) {
            return false;
        }
        for (const entry of subscriptions) {
            if (entry.unsubscribe && arraysAreEqual(entry.params.channels, [channel])) {
                return true;
            }
        }
        return false;
    }

    function getMonitor(): WsMonitor | null {
        return service.current()?.monitor ?? null;
    }

    function disconnect(): void {
        active = false;
        for (const entry of subscriptions) {
            detach(entry);
        }
        if (authBoundaryServices) {
            rotateAuthBoundaryServices();
        } else {
            service.closeIfUnused();
        }
    }

    function dispose(): void {
        if (active) {
            disconnect();
        }
        subscriptions.clear();
    }

    function resetForTest(): void {
        subscriptions.clear();
        active = false;
    }

    return {
        dispose,
        client: {
            connect,
            subscribe,
            subscribeConnectionState,
            findSubscriptionBySubscriber,
            updateSubscription,
            isWsConnected,
            isConnected,
            getMonitor,
            disconnect,
        },
        resetForTest,
    };
}

// These consumers intentionally use separate physical sockets even though
// they target the same endpoint — see the login-gating rationale on
// `legacyExchangeInternal` below. Keep the boundary explicit until the
// transport supports per-subscriber frame filtering under one connection.
const eventContractWsService = createWsServiceOwner({
    label: 'event-contract',
    getUrl: () => config.exchangeWsUrl,
});

const legacyExchangeWsService = createWsServiceOwner({
    label: 'legacy-exchange',
    getUrl: () => config.exchangeWsUrl,
});

const marginWsService = createWsServiceOwner({
    label: 'margin',
    getUrl: () => config.marginWsUrl,
});

const eventContractInternal = createWsClient<EventContractWsChannel>({
    clientName: 'event-contract',
    service: eventContractWsService,
    validChannels: EVENT_CONTRACT_WS_CHANNELS,
});

const marginInternal = createWsClient<MarginWsChannel>({
    clientName: 'margin',
    service: marginWsService,
    validChannels: MARGIN_WS_CHANNELS,
});

// Exchange-socket client consumed by userOrdersStore, userSessionStore, and
// portfolioEventsStore for login-gated user channels. Consumers can register
// their subscriptions before the client connects — even for logged-out
// visitors — but the subscriptions stay detached from the legacy service
// until connect() (post-login via userSessionStore, or tradingSync) activates
// the client. This keeps user channels (user_balance, user_orders, positions)
// invisible to the server for unauthenticated visitors even when public
// event-contract streams are already connected.
const legacyExchangeInternal = createWsClient<EventContractWsChannel>({
    clientName: 'legacy-exchange',
    service: legacyExchangeWsService,
    validChannels: EVENT_CONTRACT_WS_CHANNELS,
    connectOnSubscribe: false,
    // The event-contract socket carries both public market data and
    // authenticated user-position subscriptions. It remains physically
    // isolated from the legacy orderbook socket, but both handshakes must
    // rotate when authentication changes.
    authBoundaryServices: [legacyExchangeWsService, eventContractWsService],
});

export const eventContractWsClient: WsDomainClient<EventContractWsChannel> = eventContractInternal.client;
export const marginWsClient: WsDomainClient<MarginWsChannel> = marginInternal.client;
export const legacyExchangeWsClient: WsDomainClient<EventContractWsChannel> = legacyExchangeInternal.client;

/**
 * Full teardown for the app effects unmount disposal: detaches and
 * forgets every subscription the legacy client registered, so a rebuilt
 * app-effects mount starts clean — no dead subscriber callbacks left in the legacy
 * service. The legacy exchange socket is unconditionally closed. Plain logout
 * keeps the registrations for the next login's re-attach — use
 * `legacyExchangeWsClient.disconnect()` there.
 */
export function disposeLegacyExchangeWsClient(): void {
    legacyExchangeInternal.dispose();
}

export function _resetWsClientsForTest(): void {
    eventContractInternal.resetForTest();
    marginInternal.resetForTest();
    legacyExchangeInternal.resetForTest();
    eventContractWsService.resetForTest();
    legacyExchangeWsService.resetForTest();
    marginWsService.resetForTest();
}
